/**
 * QRShareCard: reusable QR-code share widget for ConfirmPH.
 *
 * Renders a QR code for a public URL (business profile / mini-site /
 * vehicle public page) plus actions: download PNG, copy link, print.
 *
 * IMPORTANT: privacy contract: this component never accepts a
 * dashboard or auth-gated URL. The caller is responsible for passing a
 * canonical, *publicly-resolvable* URL. We additionally strip query
 * fragments that look like dashboard tabs (defence in depth).
 */
import { useRef, useState } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { toast } from "sonner";
import { Copy, Download, Printer, Link as LinkIcon, ExternalLink } from "lucide-react";

const FORBIDDEN_PREFIXES = ["/app/", "/admin/", "/promoter/"];

function looksLikeDashboardUrl(url) {
  if (!url) return false;
  try {
    const u = new URL(url);
    return FORBIDDEN_PREFIXES.some((p) => u.pathname.startsWith(p));
  } catch {
    return FORBIDDEN_PREFIXES.some((p) => url.startsWith(p));
  }
}

export function QRShareCard({
  url,
  label = "Share this page",
  description = "Scan to open on a phone: or download / print the QR code.",
  filename = "confirmph-share",
  testIdPrefix = "qr-share",
}) {
  const wrapRef = useRef(null);
  const [copied, setCopied] = useState(false);

  if (!url) {
    return (
      <div
        data-testid={`${testIdPrefix}-disabled`}
        className="rounded-xl border border-slate-200 bg-slate-50/60 p-5 text-sm text-slate-500"
      >
        Enable your public profile first to generate a share QR code.
      </div>
    );
  }

  if (looksLikeDashboardUrl(url)) {
    // Defence in depth: we should never reach this in normal use; the
    // callers all pass an /rentals/{slug} or /business/{id} URL.
    return (
      <div
        data-testid={`${testIdPrefix}-blocked`}
        className="rounded-xl border border-red-200 bg-red-50 p-5 text-sm text-red-800"
      >
        QR sharing was disabled: the URL points at a private dashboard page.
      </div>
    );
  }

  const downloadPng = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    if (!canvas) {
      toast.error("Could not generate the QR code image.");
      return;
    }
    // Pull a slightly bigger PNG than the on-screen QR for sharper printing.
    const a = document.createElement("a");
    a.href = canvas.toDataURL("image/png");
    a.download = `${filename}.png`;
    a.click();
    toast.success("QR code downloaded");
  };

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      toast.success("Link copied to clipboard");
    } catch {
      toast.error("Could not copy: long-press to copy manually.");
    }
  };

  const printQr = () => {
    const canvas = wrapRef.current?.querySelector("canvas");
    const dataUrl = canvas ? canvas.toDataURL("image/png") : null;
    if (!dataUrl) {
      toast.error("Could not prepare the QR code for printing.");
      return;
    }
    const w = window.open("", "_blank", "width=480,height=640");
    if (!w) {
      toast.error("Pop-ups are blocked: allow pop-ups to print the QR code.");
      return;
    }
    w.document.write(`<!doctype html><html><head><title>${filename}</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,Arial,sans-serif;margin:0;padding:32px;text-align:center}
        h1{font-size:18px;margin:0 0 6px}
        .url{font-size:11px;color:#475569;word-break:break-all;margin:6px 0 20px}
        img{width:300px;height:300px;border:1px solid #e2e8f0;border-radius:8px}
      </style></head>
      <body>
        <h1>${label}</h1>
        <div class="url">${url}</div>
        <img src="${dataUrl}" alt="QR code" />
        <script>window.onload=()=>setTimeout(()=>window.print(),100);</script>
      </body></html>`);
    w.document.close();
  };

  return (
    <div
      data-testid={testIdPrefix}
      className="rounded-xl border border-slate-200 bg-white p-5 space-y-4"
    >
      <div>
        <h3 className="font-display text-base font-semibold flex items-center gap-2">
          <LinkIcon className="w-4 h-4 text-[#1B4332]" /> {label}
        </h3>
        <p className="text-xs text-slate-500 mt-0.5">{description}</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4 items-start">
        <div
          ref={wrapRef}
          className="flex-shrink-0 rounded-lg border border-slate-200 bg-white p-3"
          data-testid={`${testIdPrefix}-canvas-wrap`}
        >
          <QRCodeCanvas
            value={url}
            size={176}
            level="M"
            marginSize={2}
            fgColor="#1B4332"
          />
        </div>

        <div className="min-w-0 flex-1 space-y-2">
          <div className="rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-xs font-mono break-all">
            <span data-testid={`${testIdPrefix}-url`}>{url}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={copy}
              data-testid={`${testIdPrefix}-copy`}
              className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 text-xs inline-flex items-center gap-1.5"
            >
              <Copy className="w-3 h-3" /> {copied ? "Copied!" : "Copy link"}
            </button>
            <button
              type="button"
              onClick={downloadPng}
              data-testid={`${testIdPrefix}-download`}
              className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 text-xs inline-flex items-center gap-1.5"
            >
              <Download className="w-3 h-3" /> Download PNG
            </button>
            <button
              type="button"
              onClick={printQr}
              data-testid={`${testIdPrefix}-print`}
              className="px-3 py-1.5 rounded-md border border-slate-300 bg-white text-slate-800 hover:bg-slate-50 text-xs inline-flex items-center gap-1.5"
            >
              <Printer className="w-3 h-3" /> Print
            </button>
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              data-testid={`${testIdPrefix}-open`}
              className="px-3 py-1.5 rounded-md border border-emerald-300 bg-emerald-50 text-emerald-800 hover:bg-emerald-100 text-xs inline-flex items-center gap-1.5"
            >
              <ExternalLink className="w-3 h-3" /> Open public page
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
